import { resolveCometOrigin, resolveCometScriptUrls } from '../config/api';

type CometMessageHandler = (message: { data?: Record<string, unknown> }) => void;

declare global {
  interface Window {
    $?: {
      cometd?: CometdClient;
    };
  }
}

interface CometdClient {
  configure: (opts: { url: string; logLevel?: string }) => void;
  addListener: (channel: string, fn: (message: CometMetaMessage) => void) => void;
  removeListener: (channel: string, fn: (message: CometMetaMessage) => void) => void;
  handshake: (opts?: { callback?: (message: CometMetaMessage) => void }) => void;
  batch: (fn: () => void) => void;
  subscribe: (channel: string, fn: (message: CometMetaMessage) => void) => void;
  unsubscribe: (channel: string, fn: (message: CometMetaMessage) => void) => void;
  publish: (channel: string, data: Record<string, unknown>) => void;
  isDisconnected: () => boolean;
  disconnect: () => void;
}

interface CometMetaMessage {
  successful?: boolean;
  data?: Record<string, unknown>;
  channel?: string;
  [key: string]: unknown;
}

const SCRIPT_URLS = [
  '/resources/jquery/jquery-2.1.1.js',
  '/resources/org/cometd.js',
  '/resources/jquery/jquery.cometd.js',
];

let scriptsLoaded = false;
let scriptsLoading: Promise<void> | null = null;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`) as HTMLScriptElement | null;
    if (existing?.dataset.cometLoaded === '1') {
      resolve();
      return;
    }
    if (existing) {
      existing.remove();
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = () => {
      script.dataset.cometLoaded = '1';
      resolve();
    };
    script.onerror = () => {
      script.remove();
      reject(new Error(`Failed to load ${src}`));
    };
    document.head.appendChild(script);
  });
}

async function loadScriptWithFallback(path: string): Promise<void> {
  const candidates = path.startsWith('http') ? [path] : resolveCometScriptUrls(path);
  let lastError: Error | null = null;
  for (const src of candidates) {
    try {
      await loadScript(src);
      return;
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(`Failed to load ${src}`);
    }
  }
  throw lastError || new Error(`Failed to load ${path}`);
}

async function ensureCometScripts(): Promise<CometdClient> {
  if (!scriptsLoaded) {
    if (!scriptsLoading) {
      scriptsLoading = (async () => {
        try {
          for (const url of SCRIPT_URLS) {
            await loadScriptWithFallback(url);
          }
          scriptsLoaded = true;
        } catch (err) {
          scriptsLoading = null;
          throw err;
        }
      })();
    }
    await scriptsLoading;
  }

  const cometd = window.$?.cometd;
  if (!cometd) {
    throw new Error('CometD client not available after loading scripts');
  }
  return cometd;
}

function cometUrl(): string {
  return `${resolveCometOrigin()}/cometd`;
}

function userChannel(userId: number | string): string {
  return `/chakhna/user_channel_${userId}`;
}

function extractMessageData(message: CometMetaMessage): Record<string, unknown> | null {
  if (message.data && typeof message.data === 'object') {
    return message.data;
  }
  if (message.flag) {
    return message as Record<string, unknown>;
  }
  return null;
}

class CometService {
  private connectedUserId: number | null = null;
  private handlers = new Set<CometMessageHandler>();
  private connecting: Promise<void> | null = null;
  private channelListener: ((message: CometMetaMessage) => void) | null = null;
  private metaConnectListener: ((message: CometMetaMessage) => void) | null = null;
  private metaHandshakeListener: ((message: CometMetaMessage) => void) | null = null;
  private subscribedChannel: string | null = null;

  onMessage(handler: CometMessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  private emit(message: CometMetaMessage): void {
    const data = extractMessageData(message);
    if (!data?.flag) return;
    const payload = { data };
    this.handlers.forEach((handler) => {
      try {
        handler(payload);
      } catch (err) {
        console.error('[CometD] handler error', err);
      }
    });
  }

  async connect(userId: number): Promise<void> {
    if (!userId) return;

    if (this.connectedUserId === userId && this.subscribedChannel) {
      return;
    }

    if (this.connecting) {
      await this.connecting;
      if (this.connectedUserId === userId && this.subscribedChannel) return;
    }

    this.connecting = this.establishConnection(userId);
    try {
      await this.connecting;
    } finally {
      this.connecting = null;
    }
  }

  private async establishConnection(userId: number): Promise<void> {
    const cometd = await ensureCometScripts();

    if (this.connectedUserId != null && this.connectedUserId !== userId) {
      this.teardownCometd(cometd);
    }

    cometd.configure({ url: cometUrl(), logLevel: 'warn' });

    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        resolve();
      };

      if (!this.metaHandshakeListener) {
        this.metaHandshakeListener = (message) => {
          if (message.successful) {
            this.ensureSubscribed(cometd, userId);
          }
        };
        cometd.addListener('/meta/handshake', this.metaHandshakeListener);
      }

      if (!this.metaConnectListener) {
        this.metaConnectListener = (message) => {
          if (message.successful) {
            this.ensureSubscribed(cometd, userId);
          } else {
            this.subscribedChannel = null;
          }
        };
        cometd.addListener('/meta/connect', this.metaConnectListener);
      }

      if (!cometd.isDisconnected() && this.subscribedChannel === userChannel(userId)) {
        this.connectedUserId = userId;
        finish();
        return;
      }

      cometd.handshake({
        callback: (message) => {
          if (!message?.successful) {
            console.warn('[CometD] handshake failed', message);
            finish();
            return;
          }
          this.ensureSubscribed(cometd, userId);
          this.connectedUserId = userId;
          finish();
        },
      });
    });
  }

  private ensureSubscribed(cometd: CometdClient, userId: number): void {
    const channel = userChannel(userId);
    if (this.subscribedChannel === channel) return;

    if (!this.channelListener) {
      this.channelListener = (message) => {
        this.emit(message);
      };
    }

    cometd.batch(() => {
      if (this.subscribedChannel && this.subscribedChannel !== channel) {
        try {
          cometd.unsubscribe(this.subscribedChannel, this.channelListener!);
        } catch {
          // ignore unsubscribe errors during channel switch
        }
      }

      cometd.subscribe(channel, this.channelListener!);
      cometd.publish('/service/users', {
        flag: 'CONNECT',
        userid: `user_channel_${userId}`,
        typeOfUser: 'User',
      });
    });

    this.subscribedChannel = channel;
    this.connectedUserId = userId;
  }

  private teardownCometd(cometd: CometdClient): void {
    if (this.subscribedChannel && this.channelListener) {
      try {
        cometd.unsubscribe(this.subscribedChannel, this.channelListener);
      } catch {
        // ignore
      }
    }
    this.subscribedChannel = null;

    try {
      if (!cometd.isDisconnected()) {
        cometd.disconnect();
      }
    } catch {
      // ignore teardown errors
    }
    this.connectedUserId = null;
  }

  disconnect(): void {
    const cometd = window.$?.cometd;
    if (cometd) {
      this.teardownCometd(cometd);
    }
    this.connectedUserId = null;
  }
}

const cometService = new CometService();
export default cometService;
